import React from "react";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Filler,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import TTSDebouncer from "../../foundation/TTSDebouncer";

import "./Charts.scss";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Filler,
    Title,
    Tooltip,
    Legend,
);

const defaultBGColours: string[] = [
    "#498FBC", // blue
    "#FF7777", // red
    "#67C267", // green
    "#CCBF33", // yellow
    "#17B4A3", // turquoise
    "#C88DCE", // purple
    "#E7B065", // orange
    "#537283", // slate
    "#784D47", // burgundy
];

type TTSChartData = {
    label: string;
    data: number[];
    pointRadius?: number;
    pointBackgroundColor?: string;
    pointBorderWidth?: number;
    pointBorderColor?: string;
    borderWidth?: number;
    borderColor?: string | string[];
    backgroundColor?: string | string[];
    fill: boolean;
};

type ChartType = "Bar" | "Doughnut" | "Line";

interface TTSChartProps {
    type: ChartType;
    labels: string[];
    datasets: TTSChartData[];

    maxHeight?: string;
}

interface TTSChartrOptions {
    responsive: boolean;
    maintainAspectRatio: boolean;
    plugins: {
        legend: {
            display: boolean;
            position: "top" | "bottom" | "left" | "right";
        };
    };
}

interface TTSChartState {
    type: ChartType;
    labels: string[];
    datasets: TTSChartData[];
    options: TTSChartrOptions;

    shouldRedraw: boolean;
    wrapperElement: React.RefObject<HTMLDivElement>;
    resizeObserver: ResizeObserver;
    resizeDebouncer: TTSDebouncer;
    lastContainerWidth?: number;
}

class TTSChart extends React.Component<TTSChartProps, TTSChartState> {
    state = {
        type: this.props.type ?? "Bar",
        shouldRedraw: false,
        wrapperElement: React.createRef<HTMLDivElement>(),
        resizeObserver: new ResizeObserver(() => this.resized()),
        resizeDebouncer: new TTSDebouncer(50),
    } as TTSChartState;

    componentDidMount(): void {
        this.prepareChartData();
        this.state.resizeObserver.observe(this.state.wrapperElement.current);
    }

    componentWillUnmount(): void {
        this.state.resizeObserver.disconnect();
    }

    componentDidUpdate(prevProps: TTSChartProps): void {
        if (
            prevProps.type !== this.props.type ||
            prevProps.labels !== this.props.labels ||
            prevProps.datasets !== this.props.datasets
        ) {
            this.prepareChartData();
        }
    }

    prepareChartData() {
        let options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                    position: "top" as const,
                },
            },
        };

        if (this.props.datasets)
            this.props.datasets.forEach((dataSet, index) => {
                if (this.props.type === "Line") {
                    dataSet.pointRadius = 3;
                    dataSet.pointBorderWidth = 1;
                    dataSet.pointBorderColor = "#fff";
                    dataSet.pointBackgroundColor =
                        defaultBGColours[index % defaultBGColours.length];
                    dataSet.borderWidth = 2;
                    dataSet.borderColor =
                        defaultBGColours[index % defaultBGColours.length];
                    dataSet.backgroundColor =
                        defaultBGColours[index % defaultBGColours.length] +
                        "55";
                } else {
                    dataSet.backgroundColor = dataSet.data.map(
                        (_, index) =>
                            defaultBGColours[index % defaultBGColours.length],
                    );
                }
            });

        if (this.props.datasets.length > 1 || this.props.type === "Doughnut")
            options.plugins.legend.display = true;

        this.setState({
            type: this.props.type ?? "Bar",
            labels: this.props.labels,
            datasets: this.props.datasets.map((dataSet, index) => {
                if (this.props.type === "Line") {
                    return {
                        ...dataSet,
                        pointRadius: 3,
                        pointBorderWidth: 1,
                        pointBorderColor: "#fff",
                        pointBackgroundColor:
                            defaultBGColours[index % defaultBGColours.length],
                        borderWidth: 2,
                        borderColor:
                            defaultBGColours[index % defaultBGColours.length],
                        backgroundColor:
                            defaultBGColours[index % defaultBGColours.length] +
                            "55",
                    };
                } else {
                    return {
                        ...dataSet,
                        backgroundColor: dataSet.data.map(
                            (_, index) =>
                                defaultBGColours[
                                    index % defaultBGColours.length
                                ],
                        ),
                    };
                }
            }),
            options: options,
        });
    }

    resized() {
        const clientWidth = this.state.wrapperElement.current?.clientWidth;
        if (clientWidth === this.state.lastContainerWidth) return;

        this.state.resizeDebouncer.call(() => {
            this.setState(
                () => ({
                    shouldRedraw: true,
                    lastContainerWidth: clientWidth,
                }),
                () => this.setState(() => ({ shouldRedraw: false })),
            );
        });
    }

    render() {
        return (
            <div
                className="chart-container"
                ref={this.state.wrapperElement}
                style={{ maxHeight: this.props.maxHeight }}>
                {(this.props.type === "Bar" && (
                    <Bar
                        options={this.state.options}
                        data={{
                            labels: this.props.labels,
                            datasets: this.props.datasets,
                        }}
                        redraw={this.state.shouldRedraw}
                    />
                )) ||
                    (this.props.type === "Doughnut" && (
                        <Doughnut
                            options={this.state.options}
                            data={{
                                labels: this.props.labels,
                                datasets: this.props.datasets,
                            }}
                            redraw={this.state.shouldRedraw}
                        />
                    )) ||
                    (this.props.type === "Line" && (
                        <Line
                            options={this.state.options}
                            data={{
                                labels: this.props.labels,
                                datasets: this.props.datasets,
                            }}
                            redraw={this.state.shouldRedraw}
                        />
                    ))}
            </div>
        );
    }
}

export default TTSChart;
export { TTSChartData, TTSChartProps };
